import type { FormattedMeetingNotes } from "@/types/fireflies";

/** Known ClickUp team member IDs */
export const CLICKUP_USER_IDS = {
  beth: 10713538,
  james: 101394,
  bashie: 306881441,
  dana: 100257204,
  darel: 94467020,
  elias: 94466972,
  florisa: 282690299,
  giecris: 90771268,
  hammad: 82225816,
  jhan: 94476431,
  karen: 74419298,
  aaren: 88488459,
  oleksandr: 82435873,
  sajjad: 78201784,
  tahir: 89587106,
  tim: 306602536,
  yarra: 88432668,
} as const;

export type AssigneeSuggestion = {
  assigneeId: number;
  reason: string;
  ambiguous?: boolean;
};

type RoutingRule = {
  keywords: string[];
  assignees: number[];
  reason: string;
};

const ROUTING_RULES: RoutingRule[] = [
  {
    keywords: ["prayerlife", "prayer life"],
    assignees: [CLICKUP_USER_IDS.tim, CLICKUP_USER_IDS.oleksandr],
    reason: "PrayerLife",
  },
  {
    keywords: ["lifedaily", "life daily", "lifedaily music"],
    assignees: [CLICKUP_USER_IDS.tim],
    reason: "LifeDaily",
  },
  {
    keywords: ["book", "audiobook", "nancy"],
    assignees: [CLICKUP_USER_IDS.karen],
    reason: "Book/Audiobook",
  },
  {
    keywords: ["kapillii", "video production", "media production", "media prod"],
    assignees: [CLICKUP_USER_IDS.yarra],
    reason: "Kapillii / media production",
  },
  {
    keywords: ["ecm", "executive coordination", "scheduling"],
    assignees: [CLICKUP_USER_IDS.james],
    reason: "ECM / scheduling",
  },
  {
    keywords: ["fordham", "emba", "academic", "health informatics"],
    assignees: [CLICKUP_USER_IDS.aaren],
    reason: "Fordham / academic",
  },
  {
    keywords: ["mbx", "biometrics", "fido", "dbac", "funding", "investors"],
    assignees: [CLICKUP_USER_IDS.james, CLICKUP_USER_IDS.karen],
    reason: "MBX / funding",
  },
  {
    keywords: ["velocityhub", "velocity hub"],
    assignees: [
      CLICKUP_USER_IDS.giecris,
      CLICKUP_USER_IDS.beth,
      CLICKUP_USER_IDS.tahir,
      CLICKUP_USER_IDS.bashie,
    ],
    reason: "VelocityHub",
  },
  {
    keywords: ["dev team", "software", "github", "technical", "engineering", "bug", "deploy"],
    assignees: [CLICKUP_USER_IDS.hammad, CLICKUP_USER_IDS.sajjad, CLICKUP_USER_IDS.oleksandr],
    reason: "Dev Team / technical",
  },
  {
    keywords: ["sales", "lead", "prospect", "business development", "bd "],
    assignees: [CLICKUP_USER_IDS.jhan, CLICKUP_USER_IDS.tahir],
    reason: "Sales / business development",
  },
  {
    keywords: ["communications", "pr ", "messaging", "brand voice", "public relations"],
    assignees: [CLICKUP_USER_IDS.darel, CLICKUP_USER_IDS.elias],
    reason: "Communications / PR",
  },
  {
    keywords: ["operations", "workflow", "sop", "logistics"],
    assignees: [CLICKUP_USER_IDS.elias, CLICKUP_USER_IDS.karen],
    reason: "Operations / SOP",
  },
  {
    keywords: ["hr ", "hiring", "onboarding", "compliance", "human resources"],
    assignees: [CLICKUP_USER_IDS.karen, CLICKUP_USER_IDS.florisa],
    reason: "HR / hiring",
  },
  {
    keywords: ["payroll", "contractor payment", "finances", "finance", "payment"],
    assignees: [CLICKUP_USER_IDS.florisa],
    reason: "Payroll / finances",
  },
  {
    keywords: [
      "properties",
      "farm",
      "real estate",
      "bmc",
      "military va",
      "printing",
      "print materials",
    ],
    assignees: [CLICKUP_USER_IDS.beth],
    reason: "Properties / BMC / printing",
  },
];

function buildSearchText(notes: FormattedMeetingNotes): string {
  const parts = [
    notes.parentTaskTitle,
    notes.title,
    notes.summary,
    notes.parentTaskDescription,
    ...notes.attendees,
    ...notes.checklistItems.map((i) => i.action),
    ...notes.actionItems.map((i) => i.task),
    ...notes.topics,
  ];
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function textMatchesKeyword(text: string, keyword: string): boolean {
  const k = keyword.toLowerCase().trim();
  if (!k) return false;
  if (k.endsWith(" ")) {
    return text.includes(k);
  }
  return text.includes(k);
}

function pickAssigneeFromRule(text: string, rule: RoutingRule): number {
  for (const assigneeId of rule.assignees) {
    const memberName = Object.entries(CLICKUP_USER_IDS).find(([, id]) => id === assigneeId)?.[0];
    if (memberName && text.includes(memberName)) {
      return assigneeId;
    }
  }
  return rule.assignees[0];
}

export function suggestAssignee(notes: FormattedMeetingNotes): AssigneeSuggestion {
  const text = buildSearchText(notes);

  for (const rule of ROUTING_RULES) {
    const matched = rule.keywords.some((kw) => textMatchesKeyword(text, kw));
    if (matched) {
      const assigneeId = pickAssigneeFromRule(text, rule);
      const ambiguous = rule.assignees.length > 1;
      return {
        assigneeId,
        reason: rule.reason,
        ambiguous,
      };
    }
  }

  return {
    assigneeId: CLICKUP_USER_IDS.darel,
    reason: "Default (ownership unclear)",
    ambiguous: true,
  };
}

export function appendAmbiguityNote(description: string, suggestion: AssigneeSuggestion): string {
  if (!suggestion.ambiguous) return description;
  const note =
    "\n\n- **Assignee note:** Ownership unclear — default routing applied.";
  if (description.includes("Assignee note:")) return description;
  return description + note;
}

export function getMemberDisplayName(userId: number): string {
  const names: Record<number, string> = {
    [CLICKUP_USER_IDS.beth]: "Bethshan McLeod",
    [CLICKUP_USER_IDS.james]: "James McLeod",
    [CLICKUP_USER_IDS.bashie]: "Charis Bashie",
    [CLICKUP_USER_IDS.dana]: "Dana",
    [CLICKUP_USER_IDS.darel]: "Darel Guiwan",
    [CLICKUP_USER_IDS.elias]: "Elias Dacera",
    [CLICKUP_USER_IDS.florisa]: "Florisa (Rissa)",
    [CLICKUP_USER_IDS.giecris]: "Giecris Genterone",
    [CLICKUP_USER_IDS.hammad]: "Hammad",
    [CLICKUP_USER_IDS.jhan]: "Jhan",
    [CLICKUP_USER_IDS.karen]: "Karen",
    [CLICKUP_USER_IDS.aaren]: "Natalie Aaren Tolentino",
    [CLICKUP_USER_IDS.oleksandr]: "Oleksandr Melnyk",
    [CLICKUP_USER_IDS.sajjad]: "Sajjad Ahmad",
    [CLICKUP_USER_IDS.tahir]: "Tahir Mehmood",
    [CLICKUP_USER_IDS.tim]: "Timothy Allen Osabel",
    [CLICKUP_USER_IDS.yarra]: "Yarra Louise Lulu",
  };
  return names[userId] ?? `User ${userId}`;
}
